import { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import styleVariables from '@/styles/style.module.scss';
import { DEVICE_STATUS, DEVICE_TYPE, TAB_TYPE } from '@/types/pages/storeManagement/registration/registrationTypes';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';

dayjs.extend(utc);
dayjs.extend(timezone);

export type RegisterDeviceCardType = TAB_TYPE.AP | TAB_TYPE.RT | TAB_TYPE.TAG;

export type RegisterDeviceCardViewModel = {
    id: string;
    mac: string;
    title: string;
    deviceType: RegisterDeviceCardType;
    statusLabel: string;
    statusColor?: string;
    subStatusLabel?: string;
    isLocked?: boolean;
    confirmed?: DEVICE_STATUS;
    raw: unknown;
};
const DEVICE_TYPE_LABELS: Record<DEVICE_TYPE, string> = {
    [DEVICE_TYPE.AP]: TAB_TYPE.AP,
    [DEVICE_TYPE.RT]: TAB_TYPE.RT,
    [DEVICE_TYPE.TAG]: TAB_TYPE.TAG,
};
export const useRegisterDeviceCards = () => {
    const { state } = useRegistrationState();
    const { formatUtcToStoreTime } = useRegistrationLifecycleState();

    const cards = useMemo(() => {
        const success = styleVariables.successColor;
        const error = styleVariables.errorColor;
        return state.deviceInfoState.infraDeviceInfo.map((row) => {
            const isAP = row.device_type_id === DEVICE_TYPE.AP;
            const isRT = row.device_type_id === DEVICE_TYPE.RT;
            const isOnline = row.ed_status === 'ONLINE';
            const isLocked = row.network_mode === 'LOCK';

            const edStatusDisplay = isOnline ? 'ONLINE' : 'OFFLINE';
            const edStatusColor = isOnline ? success : error;

            const isRTUnconfirmed = isRT && row.confirmed === DEVICE_STATUS.UN_CONFIRMED;
            const edStatusLocalTime = formatUtcToStoreTime(row.ed_status_time as string);

            return {
                ...row,
                deviceTypeDisplay: normalizeDeviceType(row.device_type_id),
                isAP,
                isRT,
                isOnline,
                isLocked,
                edStatusDisplay,
                edStatusColor,
                isRTUnconfirmed,
                edStatusLocalTime,
            };
        });
    }, [state.deviceInfoState.infraDeviceInfo, formatUtcToStoreTime]);

    return {
        cards,
        isLoading: state.deviceInfoState.isLoading,
        rowCount: state.deviceInfoState.rowCount,
    };
};

const normalizeDeviceType = (value: DEVICE_TYPE): TAB_TYPE => {
    return DEVICE_TYPE_LABELS[value as DEVICE_TYPE] as TAB_TYPE;
};
